import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { HelpButton, useHelp } from "@/coquille-bridge";
import { OfficialLogo } from "@/components/OfficialLogo.jsx";
import { SyncBanner } from "@/components/SyncBanner.jsx";
import { useAuth } from "@/context/AuthContext.jsx";
import { useMarcelMode } from "@/context/MarcelMode.jsx";
import { useOrignalUi } from "@/hooks/useOrignalUi.js";

export const MAIN_NAV = [
  { id: "today", to: "/aujourdhui", key: "today", match: ["/", "/aujourdhui"] },
  { id: "territory", to: "/territoire", key: "territory", match: ["/territoire"] },
  { id: "hunt", to: "/chasse-2026", key: "hunt", match: ["/chasse-2026", "/sejour"] },
  { id: "prep", to: "/preparation", key: "prep", match: ["/preparation"] },
  { id: "tasks", to: "/taches", key: "tasks", match: ["/taches"] },
  { id: "expenses", to: "/depenses", key: "expenses", match: ["/depenses"] },
  { id: "photos", to: "/photos", key: "photos", match: ["/photos"] },
  { id: "group", to: "/groupe", key: "group", match: ["/groupe"] },
];

function HelpButtonConnected() {
  const { openHelp } = useHelp();
  const { ui } = useOrignalUi();
  return <HelpButton onClick={openHelp} testId="help-button" label={ui.app.help} />;
}

function ProfileHelpLink() {
  const { openHelp } = useHelp();
  const { ui } = useOrignalUi();
  return (
    <button type="button" onClick={() => openHelp()}>
      {ui.app.help}
    </button>
  );
}

export function profileLine(ui, user) {
  const member = user?.member || {};
  const parts = [];
  if (user?.role === "admin") parts.push(ui.group.admin);
  else if (member.kind === "associated") parts.push(ui.group.associated);
  else if (member.trip_role === "organizer") parts.push(ui.group.organizer);
  else if (member.trip_role === "hunter") parts.push(ui.group.hunter);
  if (member.duty === "food_coordinator") parts.push(ui.group.foodLead);
  return parts.filter(Boolean).join(" · ");
}

function isNavActive(item, pathname) {
  return item.match.some((p) => (p === "/" ? pathname === "/" : pathname === p || pathname.startsWith(`${p}/`)));
}

export function TopNav({ onLogout }) {
  const { name, user } = useAuth();
  const { isMarcel, mode, setMode, actingAsAdmin } = useMarcelMode();
  const { ui } = useOrignalUi();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(false);
  const current = MAIN_NAV.find((item) => isNavActive(item, location.pathname));
  const subject = current?.id === "today" ? "Chasse Portneuf 2026" : (current ? ui.nav[current.key] : ui.nav.today);
  const shortName = (name || "Marcel").split(" ")[0];

  useEffect(() => {
    setOpen(false);
    setProfile(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open && !profile) return undefined;
    function onKey(e) {
      if (e.key === "Escape") {
        setOpen(false);
        setProfile(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, profile]);

  return (
    <header className="og-chrome" data-testid="top-nav">
      <Link to="/aujourdhui" className="og-brand" data-testid="brand-link">
        <OfficialLogo size="header" decorative />
        <span className="og-brand__copy">
          <span className="og-brand__title og-brand__title--full">{ui.app.name}</span>
          <span className="og-brand__title og-brand__title--current" data-testid="nav-current">{subject}</span>
          <small>{ui.chrome.sub}</small>
        </span>
      </Link>

      <nav className="og-chrome__nav og-chrome__nav--retired" aria-label={ui.chrome.main} data-testid="main-nav">
        {MAIN_NAV.map((item) => (
          <NavLink
            key={item.id}
            to={item.to}
            className={() => `og-chrome__link${isNavActive(item, location.pathname) ? " is-active" : ""}`}
            data-testid={`nav-${item.id}`}
          >
            {ui.nav[item.key]}
          </NavLink>
        ))}
      </nav>

      <button
        type="button"
        className="og-chrome__burger"
        data-testid="nav-open"
        aria-label={ui.chrome.menu}
        aria-expanded={open}
        onClick={() => { setProfile(false); setOpen(true); }}
      >
        <span aria-hidden="true">☰</span>
        <span className="og-chrome__burger-label">{ui.chrome.menu}</span>
      </button>

        <div className="og-chrome__tools">
          {isMarcel ? (
            <div className="og-mode" data-testid="mode-switch" role="group" aria-label="Mode">
              <button type="button" className={mode === "hunter" ? "is-on" : ""} data-testid="mode-hunter" onClick={() => setMode("hunter")}>Chasseur</button>
              <button type="button" className={mode === "admin" ? "is-on" : ""} data-testid="mode-admin" onClick={() => setMode("admin")}>Admin</button>
            </div>
          ) : null}
          {isMarcel && mode === "admin" ? <span className="og-mode-pill" data-testid="mode-pill">Mode Admin</span> : null}
        <div className="og-chrome__tools-extra">
          <SyncBanner />
          <Link className="og-chrome__icon" to="/notifications" data-testid="nav-notifications">{ui.nav.notifications}</Link>
          <HelpButtonConnected />
        </div>
        <button type="button" className="og-chrome__user" data-testid="nav-profile" onClick={() => { setOpen(false); setProfile((v) => !v); }}>
          {shortName}
        </button>
        {profile ? (
          <>
          <button type="button" className="og-chrome__menu-dismiss" data-testid="profile-backdrop" aria-label={ui.app.close} onClick={() => setProfile(false)} />
          <div className="og-chrome__menu" data-testid="profile-menu">
            <p className="og-chrome__who" data-testid="profile-name">{name}</p>
            <p className="og-chrome__role" data-testid="profile-role">{profileLine(ui, actingAsAdmin ? user : { ...user, role: "member" })}</p>
            <Link to="/parametres" data-testid="nav-settings" onClick={() => setProfile(false)}>{ui.nav.settings}</Link>
            {actingAsAdmin ? <Link to="/admin" data-testid="nav-admin" onClick={() => setProfile(false)}>{ui.nav.admin}</Link> : null}
            <div className="og-chrome__menu-extra">
              <Link to="/notifications" onClick={() => setProfile(false)}>{ui.nav.notifications}</Link>
              <ProfileHelpLink />
            </div>
            <hr className="og-chrome__sep" />
            <button type="button" data-testid="nav-logout" onClick={onLogout}>{ui.app.logout}</button>
          </div>
          </>
        ) : null}
      </div>

      {open ? (
        <div className="og-nav-sheet" data-testid="mobile-nav">
          <div className="og-nav-sheet__bar">
            <strong>{ui.chrome.menu}</strong>
            <button type="button" className="og-btn" data-testid="mobile-nav-close" onClick={() => setOpen(false)}>{ui.app.close}</button>
          </div>
          <p className="og-nav-sheet__home">{ui.chrome.cockpitOnly}</p>
          <div className="og-nav-sheet__modules">
          {MAIN_NAV.map((item) => (
            <NavLink
              key={item.id}
              to={item.to}
              className={`og-nav-sheet__link${isNavActive(item, location.pathname) ? " is-active" : ""}`}
              data-testid={`mobile-nav-${item.id}`}
              onClick={() => setOpen(false)}
            >
              {ui.nav[item.key]}
            </NavLink>
          ))}
          </div>
        </div>
      ) : null}
    </header>
  );
}
